import React from "react"
import { TStepProps, TValidator } from '@health-zelas/quiz'
import { Alert, Button, Grid, Heading, Paragraph } from "@health-zelas/delta"

import { InputNumber, Radio, RequiredField, Slider } from '@packages/components'

import ProgressBar from "../../components/ProgressBar"
import Step from "../../components/Step"

import * as S from './styles'

const Preferences: React.FC<TStepProps> = ({ navigateToNextStep, navigateToPrevStep }) => {
  const accommodation = {
    name: 'acomodacao___quiz',
    validator: RequiredField,
    options: [
      {
        value: 'Enfermaria',
        label: 'Enfermaria',
        id: 'enfermaria_acomodacao_prefere',
        className: 'deseja_no_plano'
      },
      {
        value: 'Apartamento',
        label: 'Apartamento',
        id: 'apartamente_acomodacao_prefere',
        className: 'deseja_no_plano'
      }
    ]
  }
  const whereWantUse = {
    name: 'abrangencia___quiz',
    required: true,
    validator: RequiredField,
    options: [
      {
        value: 'Somente no meu Estado',
        label: 'Somente no meu estado',
        id: 'somente_estado_onde_quer_usar_o_plano',
        className: 'deseja_no_plano'
      },
      {
        value: 'Em todo o Brasil',
        label: 'Em todo o Brasil',
        id: 'em_todo_brasil_onde_quer_usar_o_plano',
        className: 'deseja_no_plano'
      }
    ]
  }
  const coParticipation = {
    name: 'coparticipacao__clonado_',
    required: true,
    validator: RequiredField,
    options: [
      {
        value: 'true',
        label: 'Sim, com coparticipação',
        id: 'com_coparticipacao',
        className: 'deseja_no_plano'
      },
      {
        value: 'false',
        label: 'Não, sem coparticipação',
        id: 'sem_coparticipacao',
        className: 'deseja_no_plano'
      }
    ]
  }
  const refund = {
    name: 'gostaria_de_ter_reembolso____simulador',
    required: true,
    validator: RequiredField,
    options: [
      {
        value: 'SIM',
        label: 'Sim, com reembolso',
        id: 'com_reembolso',
        className: 'deseja_no_plano'
      },
      {
        value: 'NÃO',
        label: 'Não, sem reembolso',
        id: 'sem_reembolso',
        className: 'deseja_no_plano'
      }
    ]
  }
  const ondonto = {
    name: 'saude_ou_odonto__clonado_',
    required: true,
    validator: RequiredField,
    options: [
      {
        value: 'Saúde e odonto (venda conjugada)',
        label: 'Sim',
        id: 'com_plano_odontologico',
        className: 'deseja_no_plano'
      },
      {
        value: 'Saúde',
        label: 'Não',
        id: 'sem_plano_odontologico',
        className: 'deseja_no_plano'
      }
    ]
  }
  const lifes = {
    name: 'n__total_de_pessoas',
    required: true,
    showTotal: true,
    validator: {
      func: (fieldValue) => !!fieldValue && Number(fieldValue) > 0,
      msg: () => 'Selecione pelo menos uma vida'
    } as TValidator,
    inputs: [
      { label: '0-18 anos', name: 'n__e_idade_0_18_anos' },
      { label: '19-23 anos', name: 'n__e_idade_19_23_anos' },
      { label: '24-28 anos', name: 'n__e_idade_24_28_anos' },
      { label: '29-33 anos', name: 'n__e_idade_29_33_anos' },
      { label: '34-38 anos', name: 'n__e_idade_34_38_anos' },
      { label: '39-43 anos', name: 'n__e_idade_39_43_anos' },
      { label: '44-48 anos', name: 'n__e_idade_44_48_anos' },
      { label: '49-53 anos', name: 'n__e_idade_49_53_anos' },
      { label: '54-58 anos', name: 'n__e_idade_54_58_anos' },
      { label: 'acima de 59', name: 'n__e_idade_59_100_anos' }
    ],
    className: 'deseja_no_plano'
  }
  const budget = {
    name: 'budget',
    min: 300,
    max: 15000,
    className: 'deseja_no_plano',
    inputId: 'valor_deseja_gastar',
    inputLabel: 'Valor',
    inputClassName: 'deseja_no_plano',
    validator: {
      func: (fieldValue = '') => {
        const valueInNumber = fieldValue.replace(/\D/g, '')

        return !!fieldValue && Number(valueInNumber) >= 300
      },
      msg: (fieldValue) => {
        if (!fieldValue) {
          return 'Campo obrigatório'
        }

        return 'Valor tem que ser no minímo R$ 300,00'
      }
    } as TValidator
  }

  return (
    <Step>
      <S.Preferences>
        <S.FullWidthGrid gap='0'>
          <Grid.Item colSpans={{ md: 6 }}>
            <S.LeftSide>
              <ProgressBar title="Simulador" fill={3} />
              <S.StepTitle as="h1" size="lg">
                Agora sim! Selecione o que deseja ter no plano.
              </S.StepTitle>
              <S.Presentation size="lg">
                São informações relevantes para gerar a sua simulação.
              </S.Presentation>
              <Alert title="Dica da Zelas Saúde:">
                Lembre-se que é importante que você escolha o que é mais interessante para o seu momento de vida! Afinal, cada item influencia diretamente no valor final do plano. <span aria-label="emoji piscando" role="img">😉</span>
              </Alert>
            </S.LeftSide>
          </Grid.Item>

          <Grid.Item colSpans={{ md: 6 }}>
            <S.RightSide>
              <Heading
                size="md"
                css={{
                  marginBottom: '$nano',
                  color: '$neutralDark',
                  fontFamily: '$secondary',
                  fontWeight: '$bold',

                  '@md': {
                    fontSize: '$md'
                  }
                }}
              >
                Qual tipo de acomodação você prefere?
              </Heading>

              <Paragraph
                size="sm"
                css={{ color: '$neutralDark', marginBottom: '$xxs' }}
              >
                Em casos de internação, fique em enfermaria (compartilhada) ou apartamento (individual)
              </Paragraph>

              <Radio {...accommodation} css={{ marginBottom: '$sm', maxWidth: '267px' }} />

              <Heading
                size="md"
                css={{
                  marginBottom: '$nano',
                  color: '$neutralDark',
                  fontFamily: '$secondary',
                  fontWeight: '$bold',

                  '@md': {
                    fontSize: '$md'
                  }
                }}
              >
                Onde você quer usar o seu plano?
              </Heading>

              <Paragraph
                size="sm"
                css={{ color: '$neutralDark', marginBottom: '$xxs' }}
              >
                Se você é uma pessoa que viaja muito, escolha por uma cobertura nacional.
              </Paragraph>

              <Radio {...whereWantUse} css={{ marginBottom: '$sm', maxWidth: '267px' }} />

              <Heading
                size="md"
                css={{
                  marginBottom: '$nano',
                  color: '$neutralDark',
                  fontFamily: '$secondary',
                  fontWeight: '$bold',

                  '@md': {
                    fontSize: '$md'
                  }
                }}
              >
                Deseja ter coparticipação?
              </Heading>

              <Paragraph
                size="sm"
                css={{ color: '$neutralDark', marginBottom: '$xxs' }}

              >
                Pague uma porcentagem dos procedimentos que realizar em troca de uma mensalidade mais barata.
              </Paragraph>

              <Radio {...coParticipation} css={{ marginBottom: '$sm', maxWidth: '267px' }} />

              <Heading
                size="md"
                css={{
                  marginBottom: '$nano',
                  color: '$neutralDark',
                  fontFamily: '$secondary',
                  fontWeight: '$bold',

                  '@md': {
                    fontSize: '$md'
                  }
                }}
              >
                Gostaria de ter reembolso?
              </Heading>

              <Paragraph
                size="sm"
                css={{ color: '$neutralDark', marginBottom: '$xxs' }}
              >
                Receba parte do valor gasto com médicos e procedimentos fora da cobertura do seu plano
              </Paragraph>

              <Radio {...refund} css={{ marginBottom: '$sm', maxWidth: '267px' }} />

              <Heading
                size="md"
                css={{
                  marginBottom: '$nano',
                  color: '$neutralDark',
                  fontFamily: '$secondary',
                  fontWeight: '$bold',

                  '@md': {
                    fontSize: '$md'
                  }
                }}
              >
                Gostaria de incluir um plano odontológico?
              </Heading>

              <Paragraph
                size="sm"
                css={{ color: '$neutralDark', marginBottom: '$xxs' }}
              >
                Tenha cobertura em mais de 200 procedimentos como exames, consultas e tratamentos
              </Paragraph>

              <Radio {...ondonto} css={{ marginBottom: '$sm', maxWidth: '267px' }} />

              <Heading
                size="md"
                css={{
                  marginBottom: '$nano',
                  color: '$neutralDark',
                  fontFamily: '$secondary',
                  fontWeight: '$bold',

                  '@md': {
                    fontSize: '$md'
                  }
                }}
              >
                Para quantas pessoas? E quais são suas idades?
              </Heading>

              <Paragraph
                size="sm"
                css={{ color: '$neutralDark', marginBottom: '$xxs' }}
              >
                Se não souber as idades de cada uma, insira a idade estimada.
              </Paragraph>

              <InputNumber {...lifes} css={{ marginBottom: '$sm', '@md': { marginBottom: '$md' } }} />

              <Heading
                size="md"
                css={{
                  marginBottom: '$nano',
                  color: '$neutralDark',
                  fontFamily: '$secondary',
                  fontWeight: '$bold',

                  '@md': {
                    fontSize: '$md'
                  }
                }}
              >
                Quanto você deseja gastar por mês?
              </Heading>

              <Paragraph
                size="sm"
                css={{ color: '$neutralDark', marginBottom: '$xxs' }}
              >
                Insira o valor final, considerando todas as pessoas inclusas no plano.
              </Paragraph>

              <Slider
                {...budget}
                css={{
                  marginBottom: 'calc($xs + $md)',

                  '@md': {
                    marginBottom: 'calc($xs + $lg)'
                  }
                }}
              />

              <S.ButtonGroup>
                <Button
                  variant="tertiary"
                  size="large"
                  id="voltar_deseja_no_plano"
                  className="deseja_no_plano"
                  onClick={navigateToPrevStep}
                >
                  Voltar
                </Button>

                <Button
                  size="large"
                  id="simular_deseja_no_plano"
                  className="deseja_no_plano"
                  onClick={navigateToNextStep}
                >
                  Simular
                </Button>
              </S.ButtonGroup>
            </S.RightSide>
          </Grid.Item>
        </S.FullWidthGrid>
      </S.Preferences>
    </Step>
  )
}

export default Preferences
